import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, ArrowRight, Clock, DollarSign } from "lucide-react";

interface ActiveProject {
  _id: string;
  projectId: string;
  projectTitle: string;
  projectCategory: string;
  projectDifficulty: string;
  status: string;
  hourlyRate: number;
  estimatedHours: string;
}

interface ActiveProjectsListProps {
  projects: ActiveProject[];
  loading?: boolean;
}

const ActiveProjectsList: React.FC<ActiveProjectsListProps> = ({
  projects,
  loading = false,
}) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case "easy":
        return "bg-green-100 text-green-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "hard":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-blue-100 text-blue-700";
      case "approved":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-32"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Active Projects</h3>
        </div>
        <Link
          to="/projects"
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View All <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Briefcase className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No active projects</p>
          <Link
            to="/projects?tab=available"
            className="text-blue-600 hover:text-blue-700 text-sm mt-2 inline-block"
          >
            Browse available projects
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.slice(0, 4).map((project) => (
            <Link
              key={project._id}
              to={`/projects/${project.projectId}`}
              className="block p-3 border border-gray-100 rounded-lg hover:border-blue-200 hover:bg-blue-50/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">
                    {project.projectTitle}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {project.projectCategory}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(project.status)}`}
                >
                  {project.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />${project.hourlyRate}/hr
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {project.estimatedHours}
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-xs ${getDifficultyColor(project.projectDifficulty)}`}
                >
                  {project.projectDifficulty}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveProjectsList;
